import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { TbBolt } from "react-icons/tb";
import { HiMagnifyingGlass } from "react-icons/hi2";
import HeroSubscribe from "./HeroSubscribe";
/* The hero is the approved preview experience — orb and chat, wired together.
   HaileyHero owns that composition; see its header for the three wires that
   join them. */
import { HaileyHero } from "./hailey/HaileyHero";
import "./hailey/hailey-hero.css";
import { SearchEvents } from "../../redux/actions/Events";

/**
 * Top of the homepage, in two bands.
 *
 * Band 1 — the classic hero: headline, search, subscribe. Leads the page.
 *
 * Band 2 — Hailey. Orb on the left at full size, chat on the right, directly
 *   below the headline. These two bands used to share a single row, which
 *   left neither enough room; splitting them gives each the full width.
 *
 * Search still wires to Redux + navigate exactly as before.
 */
export default function HomeCinematicHeroSection() {
  const [heroSearch, setHeroSearch] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const UserLocationdata = useSelector((state) => state.user.userlocation);

  const runSearch = async () => {
    const q = heroSearch.trim();
    if (!q) {
      navigate("/tickets");
      return;
    }
    navigate(`/tickets?search=${encodeURIComponent(q)}`);
    await dispatch(SearchEvents(q, UserLocationdata));
  };

  return (
    <>
      {/* ── Band 1 — headline, search, subscribe ──────────────────────── */}
      {/* Light bottom padding on purpose — band 2 below adds a large top pad
          to clear the orb's rings, so the visible gap between the two is the
          sum of both. Keeping this small stops that gap becoming a void. */}
      <section className="relative bg-black overflow-hidden pt-16 pb-6 lg:pt-20 lg:pb-8">
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 lg:gap-x-16 gap-y-10 items-center"
          >
            {/* Left — tag, headline, search */}
            <div className="min-w-0 max-w-xl flex flex-col">
              {/* Tag — pill outline hugs copy (single line, w-fit) */}
              <div className="mb-8 inline-flex w-fit max-w-full shrink-0 items-center gap-2 rounded-full border border-[#E52324]/25 bg-[#E52324]/10 px-4 py-1.5">
                <TbBolt className="h-3.5 w-3.5 shrink-0 text-[#E52324]" strokeWidth={2} aria-hidden />
                <span className="whitespace-nowrap text-[11px] font-black uppercase tracking-[0.15em] text-[#E52324]">
                  Instant Ticket Delivery
                </span>
              </div>

              {/* Headline */}
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black uppercase leading-[0.92] tracking-tight text-white mb-6">
                THE <span className="text-[#E52324]">FASTEST</span>
                <br />
                WAY TO GET
                <br />
                TICKETS.
              </h1>

              {/* Subline */}
              <p className="text-white/50 text-sm sm:text-base leading-relaxed mb-10 max-w-lg">
                Instant checkout. 100% guaranteed.
              </p>

              {/* Search bar */}
              <div className="flex gap-2 max-w-xl">
                <div className="flex-1 relative">
                  <span
                    className="pointer-events-none absolute inset-y-0 left-0 flex w-11 items-center justify-center text-white/40"
                    aria-hidden
                  >
                    <HiMagnifyingGlass className="h-4 w-4 shrink-0" strokeWidth={2} />
                  </span>
                  <input
                    type="text"
                    placeholder="Search artists, teams, venues..."
                    value={heroSearch}
                    onChange={(e) => setHeroSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        runSearch();
                      }
                    }}
                    className="w-full rounded-2xl border border-white/10 bg-white/5 py-3.5 pl-11 pr-4 text-sm text-white placeholder:text-white/30 transition-all focus:border-[#E52324]/30 focus:outline-none focus:ring-2 focus:ring-[#E52324]/30"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => runSearch()}
                  className="px-7 py-3.5 rounded-2xl bg-[#E52324] text-white text-sm font-black uppercase tracking-wider hover:bg-[#c91f20] transition-colors shadow-lg shadow-[#E52324]/20 whitespace-nowrap"
                >
                  Find Tickets
                </button>
              </div>
            </div>

            {/* Right — subscribe. The grid is items-center so this sits level
                with FASTEST rather than floating at the top of the row. */}
            <div className="min-w-0 max-w-xl lg:justify-self-end w-full">
              <HeroSubscribe />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Band 2 — Hailey. Orb left at full size, chat right.
             The top padding is load-bearing, not taste: the orb's pulse rings
             scale to 1.62 and its outer bloom reaches ~168px past the disc,
             all of which lives outside its own box. With overflow-hidden on
             this section and no clearance above, the top of the orb gets
             sliced off at the section edge. ~128px is enough for the widest
             ring at the 380px desktop size. */}
      <section className="relative bg-black overflow-hidden pt-24 lg:pt-32 pb-20 lg:pb-28">
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <HaileyHero />
          </motion.div>
        </div>
      </section>
    </>
  );
}
